import React, { ReactNode, useEffect, useState, useContext } from 'react';

import { auth } from '../config/firebase';
import {
  Auth,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signInWithPopup,
  GoogleAuthProvider,
  getAdditionalUserInfo,
  signOut,
  OAuthProvider,
  User,
  signInWithCredential,
} from 'firebase/auth';

import { extractUserInfo } from '../Utils/extract';
import useUser, { UserData } from '../hooks/useUser';
import { toast } from 'react-toastify';
import axiosInstance from '../services/axiosInstance';
import { CheckoutContext, CheckoutContextProps } from './CheckoutContext';
import { Company } from '../hooks/useCompany';
import { getFCMToken } from '../Utils/fcmToken';
import { localizeMessage } from '../Utils/dfcMessages';
// @ts-ignore
const { login } = require('webtonative/SocialLogin/google');

export interface AuthProviderProps {
  children?: ReactNode;
}

export interface AuthContextModel {
  auth: Auth;
  user: UserData | undefined;
  isAuthLoading: boolean;
  setUser: (user: UserData) => void;
  signIn: (email: string, password: string, company?: Company) => any;
  signUp: (input: UserData, company?: Company) => any;
  signInWithProvider: (provider: GoogleAuthProvider | OAuthProvider, isAndroid?: boolean, company?: Company) => any;
  resetPassword: (email: string) => Promise<boolean>;
  logout: () => void;
  deleteAccount: () => void;
  refreshUser: () => Promise<void>;
}

export const AuthContext = React.createContext<AuthContextModel>({} as AuthContextModel);

export function useAuth(): AuthContextModel {
  return useContext(AuthContext);
}

export const AuthProvider = ({ children }: AuthProviderProps): JSX.Element => {
  const [user, setUser] = useState<UserData | undefined>(undefined);
  const [isAuthLoading, setIsAuthLoading] = useState<boolean>(true);
  const { createUser, getUser, deleteUser, sendWelcomeEmail , isEmailExists, updateUser} = useUser();
  const checkoutContext = useContext(CheckoutContext) as CheckoutContextProps | null;
  const company = checkoutContext?.company;

  /**
   * Helper function to check if push notifications are supported for a company
   */
  const isPushNotificationsSupported = (companyToCheck?: Company | null): boolean => {
    return companyToCheck?.supportPushNotifications === true;
  };

  /**
   * Helper function to get FCM token if push notifications are supported
   * Returns the token or null if not supported/failed
   */
  const getFCMTokenIfSupported = async (companyToCheck?: Company | null): Promise<string | null> => {
    if (!isPushNotificationsSupported(companyToCheck)) {
      return null;
    }

    try {
      return await getFCMToken();
    } catch (error) {
      return null;
    }
  };

  /**
   * Helper function to update user with FCM token if supported
   */
  const updateUserWithFCMToken = async (
    userToUpdate: UserData,
    companyToCheck?: Company | null
  ): Promise<UserData | null> => {
    const fcmToken = await getFCMTokenIfSupported(companyToCheck);
    
    if (!fcmToken || !userToUpdate.id) {
      return null;
    }

    try {
      const updatedUser = {
        ...userToUpdate,
        fcmToken,
      };
      await updateUser(updatedUser);
      return updatedUser;
    } catch (error) {
      return null;
    }
  };

  const signUp = async (input: any, company?: Company) => {
    let firebaseUser: User | undefined;
    try {
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        input.email,
        input.password!
      );
      firebaseUser = userCredential.user;

      delete input.password;
      delete input.passwordConfirm;

      // Get FCM token if available and push notifications are supported
      const fcmToken = await getFCMTokenIfSupported(company);

      const userData: UserData = {
        ...input,
        id: firebaseUser.uid,
        tenantId: auth.tenantId!,
        isEmailVerified: company?.hasEmailVerificationRequired ? false : true,
      };

      // Only include fcmToken if we successfully got one
      if (fcmToken) {
        userData.fcmToken = fcmToken;
      }

      const createdUser = await createUser(
        userData,
        company?.hasEmailVerificationRequired ?? false
      );

      setUser(createdUser);

      // Only send welcome email if email verification is not required
      if (!company?.hasEmailVerificationRequired) {
        await sendWelcomeEmail(createdUser.id!);
      }

      return createdUser;
    } catch (error: any) {
      if (firebaseUser) {
        await firebaseUser.delete();
        toast(localizeMessage('Please try to sign up again'), {
          type: 'error',
        });
      }
      return { error };
    }
  };

  const signIn = async (email: string, password: string, company?: Company) => {
    try {
      if(!isEmailExists(email, company)) {
       return {error: 'Email already exists'}
      }
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const firebaseUser = userCredential.user;
      const user = await getUser(firebaseUser.uid);

      // Update FCM token on re-login if push notifications are supported
      const updatedUser = await updateUserWithFCMToken(user, company);
      if (updatedUser) {
        setUser(updatedUser);
        return updatedUser;
      }

      return user;
    } catch (error: any) {
      return { error: error };
    }
  };

  const resetPassword = async (email: string) => {
    try {
      await axiosInstance.post(`/users/auth/resetPassword`, {
        email,
        tenantId: auth.tenantId,
      });
      toast(localizeMessage('Check your email to reset your password'), {
        theme: 'colored',
        type: 'success',
      });
      return true;
    } catch {
      return false;
    }
  };

  const signInWithProvider = async (
    provider: GoogleAuthProvider | OAuthProvider,
    isAndroid: boolean = false,
    company?: Company
  ) => {
    try {
      if (isAndroid) {
        login({
          callback: async (value: any) => {
            console.log('Received value from Google login:', value);
            if (value.isSuccess) {
              console.log('Google login is successful, processing credentials...');
              const credential = GoogleAuthProvider.credential(value.idToken);
              console.log('credential obtained from idToken:', credential);
              const userCredential = await signInWithCredential(auth, credential);
              console.log('User credential obtained:', userCredential);
              const firebaseUser = userCredential.user;
              console.log('Firebase user:', firebaseUser);
              const userInfo = extractUserInfo(firebaseUser.providerData[0]);
              console.log('Extracted user info:', userInfo);
              const additionalUserInfo = getAdditionalUserInfo(userCredential);
              console.log('Additional user info:', additionalUserInfo);
              if (additionalUserInfo?.isNewUser) {
                console.log('User is new, creating user...');
                // Get FCM token if available and push notifications are supported
                const fcmToken = await getFCMTokenIfSupported(company);

                const userData: UserData = {
                  ...userInfo,
                  id: firebaseUser.uid,
                  tenantId: auth.tenantId!,
                  isEmailVerified: company?.hasEmailVerificationRequired ? false : true,
                  isProfileCompleted: false,
                };

                // Only include fcmToken if we successfully got one
                if (fcmToken) {
                  userData.fcmToken = fcmToken;
                }

                return await createUser(
                  userData,
                  company?.hasEmailVerificationRequired ?? false
                );
              } else {
                console.log('User exists, fetching user data...');
                const user = await getUser(firebaseUser.uid);

                // Update FCM token on re-login if push notifications are supported
                const updatedUser = await updateUserWithFCMToken(user, company);
                return updatedUser || user;
              }
            } else {
              console.error('Google login failed');
              console.log('Type:', value?.type);
              throw new Error('Google login failed');
            }
          },
        });
      } else {
        const userCredential = await signInWithPopup(auth, provider);
        const firebaseUser = userCredential.user;
        const userInfo = extractUserInfo(firebaseUser.providerData[0]);
        const additionalUserInfo = getAdditionalUserInfo(userCredential);
        if (additionalUserInfo?.isNewUser) {
          // Get FCM token if available and push notifications are supported
          const fcmToken = await getFCMTokenIfSupported(company);

          const userData: UserData = {
            ...userInfo,
            id: firebaseUser.uid,
            tenantId: auth.tenantId!,
            isEmailVerified: company?.hasEmailVerificationRequired ? false : true,
            isProfileCompleted: false,
          };

          // Only include fcmToken if we successfully got one
          if (fcmToken) {
            userData.fcmToken = fcmToken;
          }

          return await createUser(
            userData,
            company?.hasEmailVerificationRequired ?? false
          );
        } else {
          const user = await getUser(firebaseUser.uid);

          // Update FCM token on re-login if push notifications are supported
          const updatedUser = await updateUserWithFCMToken(user, company);
          return updatedUser || user;
        }
      }
    } catch (error: any) {
      return { error: error.message };
    }
  };

  const logout = async () => {
    try {
      // Remove FCM token from user document before logout if push notifications are supported
      if (user?.id && isPushNotificationsSupported(company)) {
        try {
          // Always try to clear FCM token, even if it's not currently set
          // This ensures we clean up any token that might exist on the backend
          // Use null instead of undefined so it's not filtered out by axios interceptor
          const userUpdate: UserData = {
            ...user,
            fcmToken: null as any, // Explicitly set to null to clear the token
          };
          await updateUser(userUpdate);
        } catch (error) {
          // Silently fail - FCM token removal is optional, don't block logout
        }
      }

      await signOut(auth);
      setUser(undefined);
    } catch (error) {
      toast(localizeMessage('Error while logout'));
    }
  };

  const deleteAccount = async () => {
    const currentUser = auth.currentUser;
    try {
      await deleteUser(currentUser?.uid!);
      await currentUser?.delete();
      setUser(undefined);
      toast(localizeMessage('Account Deleted Successfully'), {
        theme: 'colored',
        type: 'success',
      });
    } catch (error) {
      toast(localizeMessage('Error while Delete Account'), {
        theme: 'colored',
        type: 'error',
      });
    }
  };

  const refreshUser = async () => {
    const currentUser = auth.currentUser;
    if (currentUser) {
      try {
        const updatedUser = await getUser(currentUser.uid);
        setUser(updatedUser);
      } catch (error) {
        console.error('Error refreshing user:', error);
      }
    }
  };

  useEffect(() => {
    //function that firebase notifies you if a user is set
    const unsubscribe = auth.onAuthStateChanged(async user => {
      try {
        if (!user) {
          throw new Error();
        } else {
          if (auth.tenantId) {
            const currentUser = await getUser(user.uid);
            setUser(currentUser);

            // Update FCM token when user authenticates if push notifications are supported
            if (isPushNotificationsSupported(company) && currentUser.id) {
              const fcmToken = await getFCMTokenIfSupported(company);
              // Only update if token is different from current
              if (fcmToken && currentUser.fcmToken !== fcmToken) {
                const updatedUser = await updateUserWithFCMToken(currentUser, company);
                if (updatedUser) {
                  setUser(updatedUser);
                }
              }
            }
          }
        }
      } catch (error) {
        setUser(undefined);
      }
      setIsAuthLoading(false);
    });
    return unsubscribe;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const values = {
    signUp,
    user,
    signIn,
    resetPassword,
    auth,
    signInWithProvider,
    setUser,
    isAuthLoading,
    logout,
    deleteAccount,
    refreshUser,
  };
  return <AuthContext.Provider value={values}>{children}</AuthContext.Provider>;
};
